<template>
  <div
    class="textinput_autocomplete"
    :class="{
      'textinput_autocomplete--tray-open': optionTrayOpen,
      'textinput_autocomplete--mobile': isMobile,
    }"
    tabindex="0"
    @focus="focusHandler"
  >
    <div
      v-if="optionTrayOpen"
      class="textinput_autocomplete__overlay"
      @mousedown="closeFlow()"
    ></div>
    <div class="textinput_autocomplete__textinput_holder">
      <div class="textinput_bar">
        <TextInput
          class="textinput_autocomplete__textinput"
          v-model="inputValue"
          v-bind="textInputProps"
          ref="textInput"
          :showClearButton="optionTrayOpen && isMobile"
          @keydown.native.arrow-up.prevent="optionTrayKeyHandler"
          @keydown.native.arrow-down.prevent="optionTrayKeyHandler"
          @keydown.native.enter="keydownHandler"
          @keydown.native.tab="tabHandler"
          @keyup.native.prevent="keyupHandler"
          @keyup.native.enter.prevent="closeFlow()"
          @emptyButtonClicked="emptyButtonHandlers"
          @focus="textInputFocusHandler"
        />
        <button
          @click="closeFlow()"
          class="textinput_autocomplete__cancel_button"
        >
          Cancel
        </button>
      </div>
      <OptionTray
        class="textinput_autocomplete__tray"
        v-if="
          (optionTrayOpen && searchResults.length > 0) ||
          (forceOptionMatch && optionTrayOpen)
        "
        :options="searchResults"
        :labelNoResults="labelNoResults"
        ref="optionTray"
        @requestClose="closeFlow()"
        @setOption="overrideInputValue"
      />
      <ValidationMessage
        v-for="(inputValidationMessage, index) in inputValidationMessages"
        :key="`${index}inputValidationMessage`"
        :displayInvalid="isShowingValidationMessage"
        :validationMessage="inputValidationMessage"
      />
      <div
        @mouseup="closeFlow()"
        class="textinput_autocomplete__close_box"
      ></div>
    </div>
  </div>
</template>

<script>
import Fuse from "fuse.js";
import { setTimeout } from "timers";
import BaseComponent from "../BaseComponent";
import InputBase from "../atoms/Inputs/mixins/InputBase";
import FormMixin from "../mixins/FormMixin";
import TextInput from "../atoms/Inputs/TextInput.vue";
import OptionTray from "../atoms/OptionTray.vue";
import ValidationMessage from "../atoms/Inputs/ValidationMessage/ValidationMessage.vue";

/**
 * @group Inputs
 * This is a combination of a TextInput and an OptionTray.
 * It's a TextInput with Autocomplete.
 */
export default {
  name: "TextInputAutocomplete",
  mixins: [BaseComponent, InputBase, FormMixin],
  components: {
    TextInput,
    OptionTray,
    ValidationMessage,
  },
  data() {
    return {
      fuzzysearchOptions: {
        shouldSort: true,
        threshold: 0.4,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
      },
      searchResults: [],
      trayClosing: false,
      optionTrayOpen: false,
      cancelClose: false,
    };
  },
  props: {
    // Props passed on to the TextInput
    textInputProps: {
      type: Object,
      required: true,
    },
    // List of options that are searched
    options: {
      type: Array,
      required: true,
    },
    // Array of keys fuse.js searches on
    searchKeys: {
      type: Array,
      required: true,
    },
    // If True, user input needs to be equal to one of the options
    forceOptionMatch: {
      type: Boolean,
      default: false,
    },
    // Decides on what user input length the tray opens
    minLengthToOpenTray: {
      type: Number,
      default: 1,
    },
    required: {
      type: Boolean,
    },
    requiredMessages: {
      type: Object,
      default: () => ({
        valueIsInOptions: "Please pick one of the suggested options",
        hasValue: "This input field is required",
      }),
    },
    labelNoResults: {
      type: String,
      default: "No result",
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
  },
  computed: {
    fuseSearch() {
      const { fuzzysearchOptions } = this;
      fuzzysearchOptions.keys = this.searchKeys;
      return new Fuse(this.options, this.fuzzysearchOptions);
    },
    // Validation
    isValid() {
      return !!this.hasValue && !!this.valueIsInOptions;
    },
    inputValidationMessages() {
      if (this.enableExternalValidation)
        return [this.externalValidationErrorMessage];

      const validationMessages = [];
      Object.keys(this.requiredMessages).forEach((key) => {
        if (!this[key]) validationMessages.push(this.requiredMessages[key]);
      });
      return validationMessages;
    },
    hasValue() {
      if (!this.required) return true;
      return this.value && this.value.length > 0;
    },
    valueIsInOptions() {
      if (!this.forceOptionMatch || !this.required) return true;
      let valueIsInOptions = false;
      this.options.forEach((option) => {
        if ([option.displayValue, option.value].indexOf(this.value) > -1) {
          valueIsInOptions = true;
        }
      });
      return valueIsInOptions;
    },
  },
  methods: {
    // @vuese
    // When an option gets chosen from the drawer, this function gets called.
    overrideInputValue(option) {
      if (option) {
        this.inputValue = option.value;
        this.inputValue = option.displayValue || option.value;
        if (typeof window === "undefined") return;
        window.requestAnimationFrame(() => {
          if (!this.optionTrayOpen || !this.trayClosing) return;
          // Fire when an option from the tray is chosen and the tray closes.
          this.$emit("optionSelect", option);
        });
      }
    },
    textInputFocusHandler() {
      if (this.minLengthToOpenTray > 0) return;
      this.search();
      this.openFlow();
    },
    focusHandler() {
      this.$refs.textInput.focus();
    },
    openFlow() {
      let { inputValue } = this;
      if (inputValue === null) inputValue = "";
      if (inputValue.length >= this.minLengthToOpenTray) {
        if (this.isMobile && typeof window !== "undefined") {
          window.requestAnimationFrame(() => {
            this.optionTrayOpen = true;
          });
        } else {
          this.optionTrayOpen = true;
        }
      }
    },
    closeFlow() {
      this.trayClosing = true;
      setTimeout(() => {
        if (this.cancelClose === false) {
          if (
            this.forceOptionMatch &&
            this.inputValue &&
            this.searchResults[0] !== undefined &&
            this.inputValue.toLowerCase() ===
              this.searchResults[0].value.toLowerCase()
          ) {
            // this.overrideInputValue(this.searchResults[0]);
          } else {
            this.overrideInputValue({ value: this.inputValue });
          }
          this.optionTrayOpen = false;
          this.$refs.textInput.blur();
        }
        this.showInvalidMessage();
        this.cancelClose = false;
        this.trayClosing = false;
      }, 5);
    },
    optionTrayKeyHandler(event) {
      if (this.$refs.optionTray) {
        this.$refs.optionTray.keydownHandler(event.key);
      }
    },
    tabHandler() {
      this.closeFlow();
    },
    keydownHandler(event) {
      if (!this.optionTrayOpen || !this.$refs.optionTray) return;
      event.preventDefault();
      this.$refs.optionTray.keydownHandler(event.key);
    },
    keyupHandler(event) {
      if (event.key === "Enter") return;
      this.openFlow();
      if (["ArrowDown", "ArrowUp"].includes(event.key)) return;
      this.search();
    },
    search() {
      if (this.inputValue === "" || this.inputValue === null) {
        this.searchResults = this.options;
      } else {
        this.searchResults = this.fuseSearch.search(this.inputValue);
      }
    },
    emptyButtonHandlers() {
      this.openFlow();
      this.search();
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        this.$refs.textInput.focus();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";

.textinput_autocomplete {
  width: 100%;
  max-width: 576px;
  position: relative;
}
.textinput_autocomplete__textinput_holder {
  position: relative;
  z-index: 3;
}
.textinput_autocomplete__tray {
  position: absolute;
  display: inline-block;
  z-index: 3;
}
.textinput_autocomplete__cancel_button {
  display: none;
}
.textinput_autocomplete--mobile.textinput_autocomplete--tray-open {
  .textinput_autocomplete__textinput_holder {
    position: fixed;
    display: flex;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100vh;
    padding: 16px 8px 12px;
    z-index: 5;
    background-color: $white;
    flex-direction: column;
  }
  .textinput_bar {
    display: flex;
    flex-shrink: 0;
  }
  .textinput_autocomplete__cancel_button {
    @extend %button_typography_style;
    display: inline-block;
    color: $blue;
    background-color: transparent;
    border: 0px;
    height: 56px;
    padding: auto 0px;
  }
  .textinput_autocomplete__tray {
    position: relative;
    z-index: 5;
    max-height: calc(100% - 68px);
    overflow-x: scroll;
    bottom: 0px;
    flex-shrink: 0;
  }
}
.textinput_autocomplete__close_box {
  background-color: transparent;
  width: 100%;
  height: 100%;
  flex-shrink: 2;
}
.textinput_autocomplete__overlay {
  position: fixed;
  left: 0px;
  right: 0px;
  top: 0px;
  bottom: 0px;
  z-index: 1;
  background-color: transparent;
}
</style>
